import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (!code) {
        setStatus('error');
        setErrorMsg('No authorization code found');
        setTimeout(() => navigate('/welcome', { replace: true }), 1500);
        return;
      }

      try {
        const res = await axios.post(`${API}/auth/google/callback`, {
          code,
          redirect_uri: window.location.origin + '/auth/google/callback'
        });
        if (res.data && res.data.token) {
          localStorage.setItem('noyo_token', res.data.token);
          setStatus('success');
          
          // Clean URL and force a full reload to ensure auth state is picked up
          // This fixes the white screen issue on mobile Safari/Chrome
          window.history.replaceState(null, '', '/journal');
          
          // Small delay to ensure localStorage is committed, then reload
          setTimeout(() => {
            window.location.href = '/journal';
          }, 300);
        } else {
          setStatus('error');
          setErrorMsg('Authentication failed');
          setTimeout(() => navigate('/welcome', { replace: true }), 1500);
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        setErrorMsg(error.response?.data?.detail || 'Authentication failed');
        setTimeout(() => navigate('/welcome', { replace: true }), 1500);
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Setting up your account...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <Sparkles className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
            <p className="text-foreground font-medium">Welcome!</p>
            <p className="text-muted-foreground text-sm mt-1">Redirecting to your journal...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <p className="text-foreground font-medium">Oops!</p>
            <p className="text-muted-foreground text-sm mt-1">{errorMsg || 'Something went wrong'}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
